// ============================================================================
// EXECUTIVE / MIS DASHBOARD — top-management view
// ============================================================================
// Six widgets in a responsive grid, sharing one Period + Branch filter at the
// top. Demo build — data comes from executiveMockData.ts. The filter values
// are passed to widgets via context-friendly props (currently unused by the
// widgets — placeholder for the API-wired phase).

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutSelect } from 'components/ui';

import RecoveryBookSnapshotWidget    from './widgets/RecoveryBookSnapshotWidget';
import AgingBucketMixWidget          from './widgets/AgingBucketMixWidget';
import LitigationDraftAgingWidget    from './widgets/LitigationDraftAgingWidget';
import LitigationActiveAgingWidget   from './widgets/LitigationActiveAgingWidget';
import CasePipelineWidget            from './widgets/CasePipelineWidget';
import CaseFlowWidget                from './widgets/CaseFlowWidget';
import CashCollectedWidget           from './widgets/CashCollectedWidget';
import SlaBreachWidget               from './widgets/SlaBreachWidget';
import TopMoversWidget               from './widgets/TopMoversWidget';

import { PERIOD_OPTIONS, BRANCH_OPTIONS } from './executiveMockData';
import './dashboard.css';

const ExecutiveDashboardScreen: React.FC = () => {
  const [period, setPeriod] = useState<string>('MTD');
  const [branch, setBranch] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const actionButtons: ActionButton[] = [
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: 'Export',
      tooltip: 'Export the dashboard (XLSX) — coming next cycle',
      onClick: () => { /* hook up to Excel export when API lands */ },
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={16} />,
      label: 'Refresh',
      tooltip: 'Reload widgets',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Executive Dashboard"
        description="Recovery book, case pipeline and collection trend — at a glance."
        icon={<IconSystem.dashboard size={18} />}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        {/* Single flex-column child so .module-content's row-flex (intended for
            list+filter-panel screens) doesn't split our filter bar and grid
            into side-by-side columns. */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
          {/* ---- Filter bar ---- */}
          <div className="exec-filter-bar">
            <div style={{ minWidth: 200 }}>
              <SyutSelect
                label="Period"
                options={PERIOD_OPTIONS}
                value={period}
                onChange={(v) => setPeriod((v as string) || 'MTD')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div style={{ minWidth: 260 }}>
              <SyutSelect
                label="Branch"
                options={BRANCH_OPTIONS}
                value={branch}
                onChange={(v) => setBranch((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="exec-filter-spacer" />
            <div className="exec-demo-pill">
              <IconSystem.alert size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Demo data — not connected to live database
            </div>
          </div>

          {/* ---- Widget grid ---- */}
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '0.75rem' }}>
            <div className="exec-dashboard-grid" data-refresh={refreshKey}>
              {/* Row 1 — Book health */}
              <div className="exec-cell exec-cell-span-2">
                <RecoveryBookSnapshotWidget />
              </div>
              <div className="exec-cell exec-cell-span-2">
                <AgingBucketMixWidget />
              </div>

              {/* Row 2 — Litigation aging pair (Pending Filing + Active) */}
              <div className="exec-cell exec-cell-span-2">
                <LitigationDraftAgingWidget />
              </div>
              <div className="exec-cell exec-cell-span-2">
                <LitigationActiveAgingWidget />
              </div>

              {/* Row 3 — Case pipeline + Flow */}
              <div className="exec-cell exec-cell-span-2 exec-cell-tall">
                <CasePipelineWidget />
              </div>
              <div className="exec-cell exec-cell-span-2">
                <CaseFlowWidget />
              </div>

              {/* Row 4 — Cash + SLA */}
              <div className="exec-cell exec-cell-span-2">
                <CashCollectedWidget />
              </div>
              <div className="exec-cell exec-cell-span-2">
                <SlaBreachWidget />
              </div>

              {/* Row 5 — Top movers (full width) */}
              <div className="exec-cell exec-cell-span-4">
                <TopMoversWidget />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboardScreen;
